/**
 * Main's HIPAA audit page against the port's compliance log (FEM-80, UPL-49).
 *
 * AuditLogPage posts `AuditLog.GetAuditLogs` with keyon's vocabulary - action
 * words (VIEW, CREATE, ...), FILE_<...> resource types, keyon API names as
 * the "Function" filter, `order_by: 'timestamp'`, up to 10 000 rows from an
 * offset - and renders keyon's columns. Passed through unchanged, none of it
 * matched: the API's sorts are `occurred_at`/`id` and its pages hold 100 rows,
 * so the page's own defaults were refused (422) and every search showed an
 * empty table; and had they not been, the method names were matched against
 * request paths and the rows rendered with blank columns.
 *
 * The port records:
 *  - reads as `read` (main's VIEW); a row migrated from `hipaa_audit_log`
 *    keeps main's word, lower-cased (`view`);
 *  - main's API name, where the operation has one, as `api`: FileDownload,
 *    FileHead, FileBundle, ListCredentialDocuments, DeleteCredentialDocument,
 *    DeleteDirectiveDocument, DeleteMessageAttachment, PacmnyBeDocView. A
 *    migrated row answers `filter[api]` through its legacy `api_method`, so a
 *    question about a file spans the cutover;
 *  - main's FILE_<...> resource type as `file_type`, which the API derives
 *    from what each row recorded (pacmny-api LegacyFileType) and matches with
 *    `filter[file_type]`. The label is not translated here: the rows it
 *    depends on (a stored file's domain, a bundle's own row against its
 *    per-file rows, a message attachment's removal recorded as `message`, a
 *    pacmny-be view's class) are the API's to know, and a translation here
 *    missed two of them;
 *  - the Nursys operations by their request path (NURSYS_AUDIT_FILTERS).
 *
 * Every other Function name the page offers (GetPatientMeds, HoldMedication,
 * SendInternalMessages, ... about 110 of its 126) is NOT recorded on the
 * port's own rows: since cutover those operations are recorded by request
 * path and resource type. Such a search matches only rows migrated from
 * `hipaa_audit_log`, and the answer says so (auditFunctionNotice), because an
 * empty table read as "nobody did this" is the failure this page exists to
 * prevent. Recording main's name on those routes is the open remainder of
 * FEM-80.
 */

import api from './client';
import { NURSYS_AUDIT_FILTERS } from './nursysBridge';

/** The API's largest audit page. */
export const AUDIT_PAGE_SIZE = 100;

/** The page's own ceiling on "Records per Page". */
export const MAX_AUDIT_ROWS = 10000;

/** Main's file resource types: FILE_CREDENTIAL, FILE_CREDENTIAL_BUNDLE, FILE_PACMNY_BE_CCDA, ... */
const FILE_TYPE = /^FILE_[A-Z_]+$/i;

/**
 * Main's API names the port records on its own rows as `context.api`: the
 * stored-file reads (AuditPhiAccess: FileDownload, FileHead), the credential
 * listing and bundle, the file removals and the pacmny-be document views.
 * pacmny-api `grep -rn "'api' =>" app` lists every site; AuditFileRecordingTest
 * drives each one. A name added there belongs here.
 */
export const RECORDED_FUNCTIONS = [
  'FileDownload', 'FileHead', 'FileBundle', 'ListCredentialDocuments', 'DeleteCredentialDocument',
  'DeleteDirectiveDocument', 'DeleteMessageAttachment', 'PacmnyBeDocView',
  // #334: a stored video's CREATE row (VideoUploadService) and the note
  // readers' list of a note's videos (NoteAttachmentPresenter).
  'VideoUpload', 'ListNoteAttachments',
];

/**
 * Main's names the port records only on the files an operation stored: an
 * upload's own row carries the operation as `api_method` (FileAudit::created,
 * from the document's `created_via`), and the operation itself is recorded by
 * its request path.
 */
export const UPLOAD_FUNCTIONS = ['CreateSubAccount', 'SendInternalMessages', 'UploadNurseFile'];

/**
 * What the page should say beside a search by a Function the port does not
 * record by name, or null when it does. See the header: such a search finds
 * only history migrated from main's log.
 */
export function auditFunctionNotice(method) {
  const name = String(method || '').trim();

  if (!name || RECORDED_FUNCTIONS.includes(name) || NURSYS_AUDIT_FILTERS[name]) return null;

  if (UPLOAD_FUNCTIONS.includes(name)) {
    return `Since cutover, "${name}" is recorded only on the files it stored; the operation itself is recorded `
      + 'by request path and resource type. Rows from before cutover are found in full.';
  }

  return `Since cutover, "${name}" is not recorded by name: this search finds only activity from before cutover `
    + '(rows migrated from the legacy log). Later activity is recorded by request path and resource type; '
    + 'search by Resource Type, or leave Function blank.';
}

/** Main's action words as the port's: VIEW is a live `read` or a migrated `view`. */
export function auditActionFilter(actionType) {
  const words = String(actionType || '').split(',').map((w) => w.trim().toLowerCase()).filter(Boolean);
  const tokens = [];

  words.forEach((word) => {
    (word === 'view' ? ['view', 'read'] : [word]).forEach((token) => {
      if (!tokens.includes(token)) tokens.push(token);
    });
  });

  return tokens.join(',') || undefined;
}

/**
 * The page's filters as `/audit-events` query parameters. Every filter is
 * ANDed server-side, as main's were, so a FILE_ type and a Function naming two
 * different operations answer an empty page there.
 */
export function auditFilterQuery(p = {}) {
  const query = {};
  const resource = String(p.resource_type || '').trim();
  const method = String(p.api_method || '').trim();
  const nursys = NURSYS_AUDIT_FILTERS[method];

  const firstName = String(p.first_name || '').trim();
  const lastName = String(p.last_name || '').trim();
  if (p.user_id) query['filter[actor_id]'] = p.user_id;
  if (p.patient_id) query['filter[subject_patient_id]'] = p.patient_id;
  // Main's First Name / Last Name boxes are the PATIENT whose chart was
  // accessed (audit_log_retrieval.php `p.first_name LIKE`, `p.lastname
  // LIKE`), each its own partial match, ANDed - never the staff member who
  // acted, which is what `filter[actor_name]` matches.
  if (firstName) query['filter[patient_first_name]'] = firstName;
  if (lastName) query['filter[patient_last_name]'] = lastName;

  const action = auditActionFilter(p.action_type) || (nursys && nursys.action) || undefined;
  if (action) query['filter[action]'] = action;

  if (FILE_TYPE.test(resource)) {
    query['filter[file_type]'] = resource.toUpperCase();
  } else if (resource) {
    query['filter[resource_type]'] = resource;
  }

  if (nursys) {
    query['filter[endpoint]'] = nursys.endpoint;
  } else if (method) {
    query['filter[api]'] = method;
  }

  // Trello 6DoFcH1T's search, which main's page did not have: the trusted
  // peer address, the acting person's group, rows a security alert cites
  // (or one alert's rows), and one request by the server's X-Request-Id.
  const ip = String(p.ip_address || '').trim();
  const requestId = String(p.request_id || '').trim();
  if (ip) query['filter[ip]'] = ip;
  if (p.group_id) query['filter[group_id]'] = p.group_id;
  if (p.alert_only) query['filter[alert]'] = 1;
  if (p.alert_id) query['filter[alert_id]'] = p.alert_id;
  if (requestId) query['filter[request_id]'] = requestId;

  return query;
}

/**
 * The page's search as the API's NDJSON export (`format=json`): the whole
 * search, one row per line, saved as a file. Throttled on the API (5 a
 * minute); the server reads the log, so the browser never pages through it.
 */
export async function exportAuditLogsJson(p = {}, deps = {}) {
  const client = deps.api || api;
  const query = { ...auditFilterQuery(p), sort: auditSort(p), format: 'json' };
  if (p.start_date) query.from = String(p.start_date).slice(0, 10);
  if (p.end_date) query.to = String(p.end_date).slice(0, 10);

  return client.download('/audit-events/export', query);
}

/**
 * Main's Order By choices as the API's sorts. The API ends each on the row id
 * (pacmny-api AuditLogOrder), so a search read as pages is a total order; the
 * resource type sorts by the label the page shows (main's FILE_ label where
 * the row has one).
 */
const AUDIT_SORTS = {
  timestamp: 'occurred_at',
  id: 'id',
  action_type: 'action',
  resource_type: 'resource_type',
  api_method: 'api',
};

function auditSort(p) {
  const column = AUDIT_SORTS[String(p.order_by || '').toLowerCase()] || 'occurred_at';
  const ascending = String(p.order_dir || '').toLowerCase() === 'asc';

  return `${ascending ? '' : '-'}${column}`;
}

/** The port's action as main's word. */
function legacyAction(action) {
  const word = String(action || '');

  return word === 'read' ? 'VIEW' : word.toUpperCase();
}

/**
 * One `/audit-events` row in the columns main's page renders, the port's
 * fields kept. Main's Account ID was the tenant's account number; every row
 * the API returns is the caller's own tenant's (its OrganizationScope), so
 * it is the session's. Main's Patient Name was the accessed patient's
 * current name (`CONCAT(p.first_name, ' ', p.lastname)`), which the API
 * sends as `subject_patient_name`.
 */
export function auditRecordRow(row, context = {}) {
  const message = [row.method, row.endpoint].filter(Boolean).join(' ');

  return {
    ...row,
    account_id: context.accountNumber || '',
    user_id: row.actor_id,
    account_type: row.actor_type,
    username: row.actor_username || row.actor_name || '',
    patient_id: row.subject_patient_id,
    patient_name: row.subject_patient_name || '',
    action_type: legacyAction(row.action),
    // Main's FILE_<...> label where the row is about a file, else the port's type.
    resource_type: row.file_type || row.resource_type,
    api_method: row.api || row.endpoint || '',
    success: Boolean(row.is_successful),
    timestamp: row.occurred_at,
    // The page reads `details` as JSON and shows its `message`.
    details: JSON.stringify({
      message,
      scope: row.scope || undefined,
      break_glass_incident_id: row.break_glass_incident_id || undefined,
      request_id: row.request_id || undefined,
    }),
  };
}

/** The signed-in tenant's account number (main's Account ID), or '' when there is no session. */
async function sessionAccountNumber() {
  try {
    const { useSessionStore } = await import('@/stores/session');

    return useSessionStore().organizationNumber || '';
  } catch (e) {
    return '';
  }
}

/**
 * `AuditLog.GetAuditLogs`: `limit` rows from `offset`, read 100 at a time,
 * answered in main's envelope with the total the API counted.
 *
 * Main answered a search with one statement, so its rows agreed with each
 * other. Read as pages, every read of the log writes a row at its head (and
 * so does anyone else's activity meanwhile), which moved each later page down
 * by one: an event shown twice, another never. The first page names the
 * newest row the search covers (`meta.until_id`) and every later page is
 * read under it (`filter[until_id]`); the API ends every order on the row id,
 * so rows sharing a second keep their places. An id already shown is never
 * shown again, as a backstop.
 */
export async function readAuditLogs(p = {}, deps = {}) {
  const client = deps.api || api;
  const filters = auditFilterQuery(p);
  const limit = Math.min(Math.max(Number(p.limit) || 50, 1), MAX_AUDIT_ROWS);
  const offset = Math.max(Number(p.offset) || 0, 0);
  const base = { ...filters, sort: auditSort(p), per_page: AUDIT_PAGE_SIZE };
  if (p.start_date) base.from = String(p.start_date).slice(0, 10);
  if (p.end_date) base.to = String(p.end_date).slice(0, 10);

  const rows = [];
  const seen = new Set();
  let page = Math.floor(offset / AUDIT_PAGE_SIZE) + 1;
  let skip = offset % AUDIT_PAGE_SIZE;
  let total = 0;
  let untilId = null;

  while (rows.length < limit) {
    const query = untilId === null ? { ...base, page } : { ...base, 'filter[until_id]': untilId, page };
    // eslint-disable-next-line no-await-in-loop
    const result = await client.list('/audit-events', query);
    const items = (result && result.items) || [];
    const meta = (result && result.meta) || {};

    if (untilId === null && meta.until_id !== undefined && meta.until_id !== null) {
      untilId = meta.until_id;
    }

    total = Number(meta.total) || 0;
    items.slice(skip).forEach((item) => {
      if (seen.has(item.id)) return;
      seen.add(item.id);
      rows.push(item);
    });
    skip = 0;

    if (items.length < AUDIT_PAGE_SIZE || page >= (Number(meta.last_page) || page)) break;
    page += 1;
  }

  const accountNumber = deps.accountNumber !== undefined ? deps.accountNumber : await sessionAccountNumber();
  const records = rows.slice(0, limit).map((row) => auditRecordRow(row, { accountNumber }));
  const notice = auditFunctionNotice(p.api_method);

  return {
    status: 'success',
    records,
    total_records: total,
    returned_records: records.length,
    page: Math.floor(offset / limit) + 1,
    ...(notice ? { notice } : {}),
  };
}

export default {
  auditFilterQuery, auditActionFilter, auditRecordRow, readAuditLogs, auditFunctionNotice, exportAuditLogsJson,
};
